/* global GMAPS_API_KEY, google */
/*
 * PRD-037 — Seccion de direccion de "Configurar comercio" (app SEPARADA, V4 inline).
 *
 * Replica funcionalmente al AddressForm del panel (que esta acoplado a Redux + GoogleMapsContext +
 * @plick/ui), reusando: (a) el mismo loader de Google Places que el resto del portal
 * (useJsApiLoader + GMAPS_API_KEY), (b) el mismo mapa canonico (CommerceAddressMap) y (c) el mismo
 * mapeo de address_components → campos estructurados. Persiste sobre `commerce.address` (con lat/lng)
 * + `direccion`, que son los campos que el SETUP del dueno (`/configuracion-inicial`) espera pre-llenados.
 *
 * DOS caminos para capturar la direccion, ambos terminan llenando los campos + coordenadas:
 *   (A) Buscar la direccion en el autocompletado → se llena al elegir (el usuario ya eligio la direccion).
 *   (B) Posicionar el pin en el mapa (arranca en la geolocalizacion del partner) + "Confirmar ubicacion"
 *       → reverse-geocode del punto del pin llena coordenadas + campos.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import type { Libraries } from '@interfaces/Map';
import type { Commerce } from '@interfaces/general';
import { useDesktop } from '@utils/hooks/useDesktop';
import { CommerceAddressMap } from '@components/maps/CommerceAddressMap';
import { mapPlaceToAddress } from '@utils/address/mapPlaceToAddress';
import { V4 } from '@components/v4/tokens';
import { ErrorSummary, focusFieldOrSection } from '@components/v4/EditPageLayout';
import type { ErrorSummaryItem } from '@components/v4/EditPageLayout';
import { C } from './ui';

const LIBRARIES: Libraries = ['places'];
const FONT = 'Poppins, sans-serif';

// Ultimo recurso (si el partner niega la geolocalizacion o no esta disponible): centro de Monterrey, NL.
const FALLBACK_CENTER = { lat: 25.6866, lng: -100.3161 };

type CommerceAddress = NonNullable<Commerce['address']>;

const EMPTY_ADDRESS: CommerceAddress = {
  country: 'Mexico',
  state: '',
  city: '',
  neighborhood: '',
  postalCode: '',
  street: '',
  exteriorNumber: '',
  interiorNumber: '',
  fullAddress: '',
  latitude: 0,
  longitude: 0,
};

interface Props {
  commerce: Commerce;
  handleUpdateValue: <K extends keyof Commerce>(key: K, value: Commerce[K]) => void;
  // Del validador del contenedor (`validateAddressValues`), que es el MISMO vector que apaga
  // "Siguiente" en `ConfigureCommerce.tsx`. Se traduce fila por fila: el aviso fijo que había antes
  // nombraba seis campos y el validador exige nueve claves, así que un partner que tecleaba
  // exactamente lo que el texto le pedía se quedaba con el botón gris para siempre y sin saber
  // que lo que faltaba eran las coordenadas.
  missingValues?: string[];
}

// Ids del DOM: son el destino de cada fila del resumen. Sin ellos la fila sería un click muerto.
export const ADDRESS_SECTION_ID = 'partner-address-section';
export const ADDRESS_MAP_BLOCK_ID = 'partner-address-map';
export const ADDRESS_CONFIRM_BTN_ID = 'partner-address-confirm-location';
export const addressFieldId = (field: keyof CommerceAddress) => `partner-address-${field}`;

// Claves del validador que se corrigen tecleando, con su etiqueta y el campo al que llevan.
// `fullAddress` es DERIVADA (`updateField` la rearma con cualquier tecla), así que apunta a Calle y
// no al mapa: el camino sin Google (`handleConfirmLocation`) guarda lat/lng con los campos vacíos,
// de modo que una fila de `fullAddress` en el mapa señalaría como faltante algo ya capturado.
const ADDRESS_FIELD_ERRORS: Record<string, { field: keyof CommerceAddress; label: string }> = {
  'address.postalCode': { field: 'postalCode', label: 'Código postal' },
  'address.state': { field: 'state', label: 'Estado' },
  'address.city': { field: 'city', label: 'Ciudad' },
  'address.neighborhood': { field: 'neighborhood', label: 'Colonia' },
  'address.exteriorNumber': { field: 'exteriorNumber', label: 'Número exterior' },
  'address.street': { field: 'street', label: 'Calle' },
  'address.fullAddress': { field: 'street', label: 'Calle' },
};

// Las dos coordenadas se arreglan con el MISMO gesto, así que rinden una sola fila.
const ADDRESS_COORDINATE_KEYS = ['address.latitude', 'address.longitude'];

type AddressErrorItem = ErrorSummaryItem & { sectionId: string };

// `locationTargetId` lo decide el componente: el botón "Confirmar ubicacion" nace deshabilitado
// mientras Google Maps no carga, y `.focus()` sobre un control deshabilitado no falla — no hace
// nada. En ese caso la fila lleva al bloque del mapa, que sí recibe foco.
export const buildAddressErrorItems = (missingValues: string[], locationTargetId: string): Array<AddressErrorItem> => {
  const items: Array<AddressErrorItem> = [];
  const seen = new Set<string>();
  const push = (item: AddressErrorItem) => {
    // `ErrorSummary` usa `fieldId` como key de React: dos claves que llevan al mismo control
    // (lat + lng, o calle + fullAddress) tienen que colapsar en una sola fila.
    if (seen.has(item.fieldId)) return;
    seen.add(item.fieldId);
    items.push(item);
  };

  missingValues.forEach((key) => {
    const fieldError = ADDRESS_FIELD_ERRORS[key];
    if (fieldError) {
      push({
        fieldId: addressFieldId(fieldError.field),
        label: fieldError.label,
        sectionLabel: 'Dirección del negocio',
        sectionId: ADDRESS_SECTION_ID,
      });
      return;
    }
    if (ADDRESS_COORDINATE_KEYS.includes(key)) {
      push({
        fieldId: locationTargetId,
        label: 'Ubicación en el mapa',
        sectionLabel: 'Busca la dirección o confirma el pin — teclear los campos no la captura',
        sectionId: ADDRESS_MAP_BLOCK_ID,
      });
    }
  });

  return items;
};

const inputStyle: CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  fontSize: 14,
  fontFamily: FONT,
  color: C.primary,
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 6,
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: C.primary,
  marginBottom: 6,
};

const confirmBtnStyle: CSSProperties = {
  width: '100%',
  height: 44,
  marginTop: 8,
  border: 'none',
  borderRadius: 8,
  background: V4.secondary,
  color: V4.white,
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  cursor: 'pointer',
};

// Reconstruye fullAddress a partir de los campos estructurados (mismo formato que el panel).
const buildFullAddress = (a: CommerceAddress): string =>
  `${a.street} ${a.exteriorNumber}${a.interiorNumber ? ` Int. ${a.interiorNumber}` : ''}, ${a.neighborhood}, ${
    a.postalCode
  }, ${a.city}, ${a.state}, ${a.country}`;

export const ConfigureAddressSection = ({ commerce, handleUpdateValue, missingValues = [] }: Props) => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GMAPS_API_KEY, libraries: LIBRARIES });
  const isDesktop = useDesktop();

  const addr: CommerceAddress = { ...EMPTY_ADDRESS, ...(commerce.address || {}) };
  const hasCoords = addr.latitude !== 0 && addr.longitude !== 0;
  // "Ubicacion capturada ✓" solo cuando de verdad hay datos: coordenadas + al menos un campo estructurado.
  const hasCapturedLocation = hasCoords && Boolean(addr.postalCode || addr.city || addr.state || addr.street);

  // Posicion del pin (estado LOCAL: el arrastre es inmediato, sin snap-back). Arranca en las coordenadas
  // guardadas o el fallback; la geolocalizacion inicial y la busqueda la actualizan.
  const [markerCenter, setMarkerCenter] = useState<{ lat: number; lng: number }>(
    hasCoords ? { lat: addr.latitude, lng: addr.longitude } : FALLBACK_CENTER,
  );
  const [confirming, setConfirming] = useState(false);
  // Feedback de la ultima confirmacion: antes el boton volvia a su texto original sin importar si
  // el geocode funciono o fallo (sin exito visible mas alla de una linea aparte, sin error para
  // nada) — el partner no sabia si de verdad paso algo. 'success' es transitorio (el check verde
  // persistente de abajo ya cubre el estado estable); 'error' se queda hasta el proximo intento.
  const [confirmResult, setConfirmResult] = useState<'idle' | 'success' | 'error'>('idle');
  const userMovedRef = useRef(false);
  // `addr` se recalcula en cada render desde `commerce.address` — el callback async del geocoder
  // (handleConfirmLocation) captura por closure el `addr` del render en que se disparó. Si el
  // partner edita un campo A MANO mientras el geocode sigue en vuelo, ese edit sí actualiza
  // `commerce.address` (re-render), pero el callback stale seguiría viendo el `addr` viejo y, si el
  // geocode falla, pisaría la edición reciente con datos obsoletos. `addrRef` siempre apunta a la
  // ÚLTIMA `addr` (se reasigna en cada render), así el callback async lee el valor vigente al
  // resolver, no el capturado al iniciar.
  const addrRef = useRef(addr);
  addrRef.current = addr;
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  // Geolocalizacion inicial: si el comercio no tiene coordenadas y el partner aun no movio el pin,
  // centra en su ubicacion ACTUAL (la mayoria da de alta un negocio cercano / de su misma ciudad).
  // Monterrey queda de fallback si niega el permiso. Solo mueve el centro/pin inicial, no captura.
  useEffect(() => {
    if (hasCoords || userMovedRef.current || typeof navigator === 'undefined' || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!userMovedRef.current) setMarkerCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }, [hasCoords]);

  // Aplica un address mapeado (de Autocomplete o de reverse-geocode) a los campos + fullAddress +
  // direccion. Conserva el numero interior escrito a mano (Google nunca lo devuelve) y permite forzar
  // las coordenadas exactas (las del pin) por encima de las que resuelva el geocoder.
  const applyMappedAddress = (mapped: CommerceAddress, coords?: { lat: number; lng: number }) => {
    const next: CommerceAddress = {
      ...mapped,
      interiorNumber: addrRef.current.interiorNumber || mapped.interiorNumber,
      latitude: coords ? coords.lat : mapped.latitude,
      longitude: coords ? coords.lng : mapped.longitude,
    };
    handleUpdateValue('address', next);
    // `mapped.fullAddress` viene de `place.formatted_address`, que el Geocoder no siempre incluye
    // (reverse-geocode de puntos con datos parciales). Sin este fallback, `direccion` se quedaba con
    // el valor ANTERIOR mientras los campos estructurados (calle, colonia, cp...) ya mostraban los
    // nuevos — inconsistente. `buildFullAddress` reconstruye el string desde los campos que SÍ
    // acaban de actualizarse, igual que hace `updateField` para ediciones manuales.
    handleUpdateValue('direccion', next.fullAddress || buildFullAddress(next));
  };

  // Arrastrar SOLO reposiciona el pin (inmediato, sin capturar); el partner confirma con el boton.
  const handleMarkerDragEnd = (lat: number, lng: number) => {
    userMovedRef.current = true;
    setMarkerCenter({ lat, lng });
  };

  // Elegir una sugerencia llena los campos (el usuario ya eligio la direccion) + mueve el pin.
  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    userMovedRef.current = true;
    const mapped = mapPlaceToAddress(place);
    applyMappedAddress(mapped);
    if (mapped.latitude && mapped.longitude) setMarkerCenter({ lat: mapped.latitude, lng: mapped.longitude });
  };

  // "Confirmar ubicacion": reverse-geocode del punto del pin → coordenadas exactas + campos de direccion.
  const handleConfirmLocation = () => {
    userMovedRef.current = true;
    setConfirmResult('idle');
    if (typeof google === 'undefined' || !google.maps) {
      handleUpdateValue('address', { ...addrRef.current, latitude: markerCenter.lat, longitude: markerCenter.lng });
      setConfirmResult('error');
      return;
    }
    setConfirming(true);
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: markerCenter }, (results, status) => {
      setConfirming(false);
      if (status === 'OK' && results && results[0]) {
        applyMappedAddress(mapPlaceToAddress(results[0]), markerCenter);
        setConfirmResult('success');
        window.setTimeout(() => setConfirmResult('idle'), 2500);
      } else {
        // Usa addrRef (valor VIGENTE), no `addr` capturado al iniciar el geocode — evita pisar
        // ediciones manuales hechas mientras la llamada async seguía en vuelo (ver comentario en addrRef).
        handleUpdateValue('address', { ...addrRef.current, latitude: markerCenter.lat, longitude: markerCenter.lng });
        setConfirmResult('error');
      }
    });
  };

  // Edicion manual de un campo: recalcula fullAddress + direccion (conserva lat/lng capturadas).
  const updateField = (key: keyof CommerceAddress, value: string) => {
    const next: CommerceAddress = { ...addr, [key]: value };
    const fullAddress = buildFullAddress(next);
    handleUpdateValue('address', { ...next, fullAddress });
    handleUpdateValue('direccion', fullAddress);
  };

  // Funcion (no componente) que retorna JSX: evita el remount/perdida de foco que provocaria un
  // componente declarado dentro del render (identidad nueva en cada re-render por keystroke).
  const renderField = (label: string, field: keyof CommerceAddress, placeholder?: string, width?: string | number) => (
    <label style={{ display: 'block', marginBottom: 14, width }}>
      <span style={labelStyle}>{label}</span>
      <input
        id={addressFieldId(field)}
        style={inputStyle}
        placeholder={placeholder}
        value={(addr[field] as string) || ''}
        onChange={(e) => updateField(field, e.target.value)}
      />
    </label>
  );

  // Ver `buildAddressErrorItems`: con el mapa aún cargando (o con el geocode en vuelo) el botón está
  // deshabilitado, y una fila que enfoca un botón deshabilitado promete llevar a algo y no lleva.
  const locationTargetId = isLoaded && !confirming ? ADDRESS_CONFIRM_BTN_ID : ADDRESS_MAP_BLOCK_ID;
  const errorItems = buildAddressErrorItems(missingValues, locationTargetId);

  return (
    <div id={ADDRESS_SECTION_ID} tabIndex={-1} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px' }}>
        Pre-llena la direccion del negocio. El dueno solo la confirmara en su configuracion inicial.
      </p>

      <div id={ADDRESS_MAP_BLOCK_ID} tabIndex={-1} style={{ marginBottom: 14 }}>
        <span style={labelStyle}>Buscar direccion</span>
        <CommerceAddressMap
          isLoaded={isLoaded}
          center={markerCenter}
          hasSavedLocation={hasCoords}
          markerPosition={markerCenter}
          onPlaceSelected={handlePlaceSelected}
          onMarkerDragEnd={handleMarkerDragEnd}
          searchPlaceholder="Escribe y elige la direccion del negocio"
          height={isDesktop ? 280 : 200}
        />
        <span style={{ display: 'block', fontSize: 11, color: C.muted, marginTop: 6 }}>
          Busca la direccion arriba, o mueve el marcador azul al punto exacto del negocio y toca Confirmar ubicacion.
          Los campos de abajo se llenan solos.
        </span>
        <button
          id={ADDRESS_CONFIRM_BTN_ID}
          type="button"
          style={{
            ...confirmBtnStyle,
            background: confirmResult === 'success' ? V4.success : confirmBtnStyle.background,
            opacity: confirming || !isLoaded ? 0.6 : 1,
          }}
          onClick={handleConfirmLocation}
          disabled={confirming || !isLoaded}
        >
          {confirming
            ? 'Cargando direccion…'
            : confirmResult === 'success'
              ? 'Ubicacion confirmada ✓'
              : 'Confirmar ubicacion del negocio'}
        </button>
        {hasCapturedLocation && (
          <span style={{ display: 'block', fontSize: 12, color: C.success, marginTop: 8 }}>Ubicacion capturada ✓</span>
        )}
        {confirmResult === 'error' && (
          <span style={{ display: 'block', fontSize: 12, color: C.warning, marginTop: 8, lineHeight: 1.4 }}>
            No pudimos obtener la direccion completa de ese punto automaticamente. Guardamos la ubicacion del pin —
            completa los campos de abajo a mano, o intenta moviendo el marcador a un punto sobre una calle reconocida y
            confirma de nuevo.
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {renderField('Codigo postal', 'postalCode', '12345', 120)}
        <div style={{ flex: 1, minWidth: 160 }}>{renderField('Estado', 'state', 'Nuevo Leon')}</div>
      </div>
      {renderField('Ciudad', 'city', 'Monterrey')}
      {renderField('Colonia', 'neighborhood', 'Centro')}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160 }}>{renderField('Numero exterior', 'exteriorNumber', '110')}</div>
        <div style={{ flex: 1, minWidth: 160 }}>{renderField('Numero interior', 'interiorNumber', 'B (opcional)')}</div>
      </div>
      {renderField('Calle', 'street', 'Calle Principal')}

      {errorItems.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <ErrorSummary
            items={errorItems}
            summaryRef={errorSummaryRef}
            onItemClick={(item) => focusFieldOrSection(item.fieldId, item.sectionId)}
          />
        </div>
      )}
    </div>
  );
};

export default ConfigureAddressSection;
